
import { encryptData, decryptData, generateSignature, verifySignature } from './encryptionService';

const LOCAL_STORAGE_PREFIX = 'bluetooth$_';
const SIGNATURE_SUFFIX = '_sig';

/**
 * Securely stores data in the device's local storage with encryption and signature
 */
export const secureStore = async (key: string, data: any): Promise<void> => {
  try {
    // Encrypt the data
    const encryptedData = await encryptData(data);
    
    // Generate a signature for the encrypted data
    const signature = await generateSignature(encryptedData, 'local-storage-key');
    
    // Store both encrypted data and signature
    localStorage.setItem(`${LOCAL_STORAGE_PREFIX}${key}`, encryptedData);
    localStorage.setItem(`${LOCAL_STORAGE_PREFIX}${key}${SIGNATURE_SUFFIX}`, signature);
    
    console.log(`Data securely stored locally with key: ${key}`);
  } catch (error) {
    console.error('Error storing data locally:', error);
    throw new Error('Failed to store data securely');
  }
};

/**
 * Retrieves and decrypts data from the device's local storage with signature verification
 */
export const secureRetrieve = async <T>(key: string): Promise<T | null> => {
  try {
    const encryptedData = localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${key}`);
    const signature = localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${key}${SIGNATURE_SUFFIX}`);
    
    if (!encryptedData || !signature) {
      return null;
    }
    
    // Verify the signature before decrypting
    if (!(await verifySignature(encryptedData, signature, 'local-storage-key'))) {
      throw new Error('Data integrity check failed');
    }
    
    return await decryptData(encryptedData) as T;
  } catch (error) {
    console.error('Error retrieving data from local storage:', error);
    // Remove potentially corrupted data
    secureRemove(key);
    return null;
  }
};

/**
 * Removes sensitive data and its signature from local storage
 */
export const secureRemove = (key: string): void => {
  try {
    localStorage.removeItem(`${LOCAL_STORAGE_PREFIX}${key}`);
    localStorage.removeItem(`${LOCAL_STORAGE_PREFIX}${key}${SIGNATURE_SUFFIX}`);
    console.log(`Removed data with key: ${key} from local storage`);
  } catch (error) {
    console.error('Error removing data from local storage:', error);
  }
};

/**
 * Checks if data exists in local storage and verifies its integrity
 */
export const dataExists = async (key: string): Promise<boolean> => {
  const encryptedData = localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${key}`);
  const signature = localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${key}${SIGNATURE_SUFFIX}`);
  
  if (!encryptedData || !signature) {
    return false;
  }
  
  try {
    return await verifySignature(encryptedData, signature, 'local-storage-key');
  } catch {
    return false;
  }
};
